import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Phone, Clock, ShieldCheck, Plane, Sparkles, MessagesSquare } from "lucide-react";
import { Reveal, Overline, MaskedLines } from "./Motion";
import { EstimateForm } from "./EstimateForm";
import { BIZ, ROOF_OPTIONS, STEPS } from "../data/site";
import { TOWNS } from "../data/areas";

export const Marquee = ({ items }) => (
  <div className="overflow-hidden border-y border-slate-200 bg-slate-100 py-5" data-testid="marquee">
    <div className="marquee-track flex w-max">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center" aria-hidden={k === 1}>
          {items.map((t) => (
            <span key={t} className="font-display flex items-center whitespace-nowrap px-8 text-3xl font-black uppercase italic tracking-tight text-slate-950 md:text-5xl">
              {t}<span className="ml-16 inline-block h-3 w-3 rotate-45 bg-slate-950" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const WHY = [
  { i: Plane, t: "Free drone inspection", d: "See exactly what we see, with clear photos of your roof." },
  { i: MessagesSquare, t: "Straight answers", d: "Options explained in plain language, never a pressure pitch." },
  { i: ShieldCheck, t: "Licensed & BBB A rated", d: "Wisconsin licensed and BBB Accredited with an A rating." },
  { i: Sparkles, t: "Spotless clean-up", d: "Landscaping protected and every nail picked up." },
];

export const WhyUs = ({ img }) => (
  <section className="bg-white py-24 md:py-32" data-testid="why-us-section">
    <div className="wrap grid items-center gap-12 lg:grid-cols-12">
      <Reveal className="relative lg:col-span-5">
        <div className="overflow-hidden rounded-2xl"><img loading="lazy" decoding="async" src={img} alt="Our crew at work" className="aspect-[4/5] w-full object-cover transition-transform duration-[1.5s] hover:scale-105" /></div>
        <div className="absolute -right-3 top-8 rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-slate-200 md:-right-8">
          <span className="font-display block text-4xl font-black leading-none text-slate-900">5.0★</span>
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Rated on Google</span>
        </div>
      </Reveal>
      <div className="lg:col-span-7">
        <Reveal><Overline>Why homeowners choose us</Overline>
          <h2 className="font-display text-4xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">The Good Home difference</h2></Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {WHY.map((w, i) => (
            <Reveal key={w.t} delay={i * 0.07}>
              <div className="group h-full rounded-2xl border border-slate-200 p-6 transition-[background-color,border-color] duration-300 hover:border-slate-900 hover:bg-slate-900" data-testid={`why-${i}`}>
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-slate-100 text-slate-900 transition-colors group-hover:bg-white"><w.i className="h-6 w-6" /></span>
                <h3 className="font-display mt-5 text-2xl font-bold uppercase text-slate-900 group-hover:text-white">{w.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 group-hover:text-slate-300">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const RoofOptions = () => {
  const [active, setActive] = useState(ROOF_OPTIONS[0].key);
  const opt = ROOF_OPTIONS.find((o) => o.key === active);
  return (
    <section className="bg-slate-50 py-24 md:py-32" data-testid="roof-options-section">
      <div className="wrap">
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Overline>Roof options</Overline>
            <h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-slate-900 md:text-7xl">What's it gonna cost me?</h2>
            <p className="mt-6 max-w-md text-slate-600">Every roof is different. Pick a material to compare, then get a free, no-pressure estimate for your home.</p>
            <div className="mt-8 grid grid-cols-2 gap-2">
              {ROOF_OPTIONS.map((o) => (
                <button key={o.key} onClick={() => setActive(o.key)} data-testid={`roof-option-tab-${o.key}`}
                  className={`rounded-xl border px-4 py-4 text-left transition-[background-color,border-color,color] duration-300 ${active === o.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-800 hover:border-slate-900"}`}>
                  <span className="font-display block text-lg font-bold uppercase leading-tight">{o.label}</span>
                  <span className={`text-xs ${active === o.key ? "text-amber-400" : "text-slate-500"}`}>Typical life {o.life}</span>
                </button>
              ))}
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <div className="dark-ctx relative overflow-hidden rounded-2xl bg-slate-900 text-white" data-testid="roof-option-panel">
              <motion.img key={opt.key} src={opt.img} alt={opt.label} initial={{ opacity: 0.4, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }} className="h-72 w-full object-cover md:h-96" />
              <div className="p-6 md:p-10">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <h3 className="font-display text-4xl font-black uppercase">{opt.label}</h3>
                  <span className="rounded-full bg-amber-500 px-4 py-1 text-sm font-bold text-slate-950">{opt.life}</span>
                </div>
                <p className="mt-4 max-w-xl text-slate-300">{opt.text}</p>
                <div className="mt-5 flex flex-wrap gap-2">{opt.points.map((p) => <span key={p} className="rounded-full border border-white/20 px-3 py-1 text-xs font-semibold text-slate-200">{p}</span>)}</div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/contact" className="btn-amber" data-testid="roof-option-estimate-button">Get my roof price <ArrowRight className="h-4 w-4" /></Link>
                  <a href={BIZ.tel} className="btn-ghost-light" data-testid="roof-option-call-button"><Phone className="h-4 w-4" />{BIZ.phone}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Process = () => (
  <section className="bg-white py-24 md:py-32" data-testid="process-section">
    <div className="wrap">
      <Reveal><Overline>How it works</Overline>
        <h2 className="font-display max-w-3xl text-5xl font-black uppercase leading-[0.9] text-slate-900 md:text-7xl">Four simple steps. Zero runaround.</h2></Reveal>
      <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08} className="h-full">
            <div className="group h-full bg-white p-8 transition-colors duration-300 hover:bg-slate-900" data-testid={`process-step-${s.n}`}>
              <span className="font-display text-6xl font-black text-amber-500">{s.n}</span>
              <h3 className="font-display mt-6 text-2xl font-bold uppercase text-slate-900 group-hover:text-white">{s.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 group-hover:text-slate-300">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ServiceAreas = () => (
  <section className="bg-white pb-24 md:pb-32" data-testid="service-areas-section">
    <div className="wrap grid items-end gap-10 border-t border-slate-200 pt-16 lg:grid-cols-12">
      <Reveal className="lg:col-span-5"><Overline>Service area</Overline>
        <h2 className="font-display text-4xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">Proudly serving Wisconsin homeowners</h2></Reveal>
      <Reveal className="lg:col-span-7" delay={0.1}>
        <div className="flex flex-wrap gap-2">
          {TOWNS.map((t) => (
            <Link key={t.slug} to={`/service-areas/${t.slug}`} className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 transition-colors hover:border-amber-500 hover:bg-amber-50" data-testid={`area-${t.slug}`}>
              <MapPin className="h-3.5 w-3.5 text-amber-600" />{t.name}
            </Link>
          ))}
          <Link to="/service-areas" className="rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white" data-testid="area-all-link">+ nearby towns</Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export const EstimateSection = ({ service, address = "", title = ["Get your", "free estimate"] }) => (
  <section id="estimate" className="dark-ctx relative overflow-hidden bg-slate-950 py-24 text-white md:py-32" data-testid="estimate-section">
    <div className="grain pointer-events-none absolute inset-0" />
    <div className="wrap relative grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Overline light>Free · No pressure</Overline>
        <h2 className="font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl"><MaskedLines lines={title} inView /></h2>
        <p className="mt-6 max-w-sm text-slate-300">Tell us a little about your project and we'll get back to you to set up a free inspection and estimate.</p>
        <div className="mt-10 space-y-4">
          <a href={BIZ.tel} className="flex items-center gap-4 text-slate-200 hover:text-amber-400" data-testid="estimate-section-call"><span className="grid h-12 w-12 place-items-center rounded-full bg-amber-500 text-slate-950"><Phone className="h-5 w-5" /></span><span><span className="block text-xs uppercase tracking-widest text-slate-400">Call or text</span><span className="font-display text-2xl font-bold">{BIZ.phone}</span></span></a>
          <div className="flex items-center gap-4 text-slate-200"><span className="grid h-12 w-12 place-items-center rounded-full border border-white/20"><Clock className="h-5 w-5 text-amber-400" /></span><span className="text-sm">Quick response on every request</span></div>
        </div>
      </div>
      <Reveal className="lg:col-span-7" delay={0.1}>
        <div className="rounded-2xl bg-white p-6 text-slate-900 shadow-2xl md:p-10"><EstimateForm defaultService={service} defaultAddress={address} /></div>
      </Reveal>
    </div>
  </section>
);
